import {ActionList, ActionMenu, Button, IconButton} from '@primer/react'
import {LockIcon, OrganizationIcon, PersonIcon, TriangleDownIcon} from '@primer/octicons-react'
import {clsx} from 'clsx'
import {useMemo, useState, type ReactNode} from 'react'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {generateSSOUrl} from '@github-ui/global-sso-banner-ui/generate-sso-url'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useLocation} from '@github-ui/react-router'
import {useMediaQuery} from '@github-ui/use-media-query'

import type {AvailableOrganization, TaskComposerScope} from '@github-ui/one-copilot-composer/shared'
import {navigateToSsoUrl} from './navigate-to-sso-url'
import {setPendingSsoOrgLogin} from '@github-ui/one-copilot-composer/pending-sso-org'
import styles from './ScopePicker.module.css'

/**
 * Leading visual for the trigger and menu items. Renders the entity avatar when
 * available, falling back to an octicon matching the entity kind.
 */
function ScopeAvatar({
  src,
  alt,
  kind,
  size,
}: {
  src: string | undefined
  alt: string
  kind: 'user' | 'organization'
  size: number
}) {
  if (src) {
    return <GitHubAvatar src={src} alt={alt} size={size} square={kind === 'organization'} />
  }
  const FallbackIcon = kind === 'organization' ? OrganizationIcon : PersonIcon
  return <FallbackIcon size={size} aria-label={alt} />
}

interface ScopePickerProps {
  /** Currently selected scope. */
  selectedScope: TaskComposerScope
  /** Called when the user picks a new scope. Not called when SSO gating intervenes. */
  onSelectScope: (scope: TaskComposerScope) => void
  /** Retained unavailable owner that remains effective until the user selects another scope. */
  retainedOwnerLogin?: string
  /** Organizations the user can pick from. */
  organizations: AvailableOrganization[]
  /** Current user login, used for the personal-scope label + avatar fallback. */
  currentUserLogin: string
  /** Current user avatar URL, used for the personal-scope leading visual. */
  currentUserAvatarUrl?: string
  /** Optional class applied to the trigger button. */
  triggerClassName?: string
  /** Optional disabled flag forwarded to the trigger button. */
  disabled?: boolean
  /** When true, force the icon-only trigger even at wide viewports. */
  forceIconOnly?: boolean
  /** Trigger size, matched to the surface that contains the picker. */
  size?: 'small' | 'medium'
}

/**
 * Scope selector for the TaskComposer. Selecting an org that requires SSO opens
 * the SSO flow with `return_to` set to the current URL; the caller re-hydrates
 * scope on return.
 */
export function ScopePicker({
  selectedScope,
  onSelectScope,
  retainedOwnerLogin,
  organizations,
  currentUserLogin,
  currentUserAvatarUrl,
  triggerClassName,
  disabled,
  forceIconOnly = false,
  size = 'medium',
}: ScopePickerProps) {
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const isNarrow = useMediaQuery('(max-width: calc(80rem - 0.02px))')
  const showIconOnly = forceIconOnly || isNarrow

  const {triggerLabel, triggerAvatarSrc, triggerAvatarAlt} = useMemo(() => {
    if (retainedOwnerLogin) {
      return {
        triggerLabel: retainedOwnerLogin,
        triggerAvatarSrc: undefined,
        triggerAvatarAlt: `${retainedOwnerLogin} avatar`,
      }
    }
    if (selectedScope.kind === 'organization') {
      const org = selectedScope.organization
      return {triggerLabel: org.login, triggerAvatarSrc: org.avatarUrl, triggerAvatarAlt: `${org.login} avatar`}
    }
    return {
      triggerLabel: currentUserLogin,
      triggerAvatarSrc: currentUserAvatarUrl,
      triggerAvatarAlt: `${currentUserLogin} avatar`,
    }
  }, [selectedScope, currentUserLogin, currentUserAvatarUrl, retainedOwnerLogin])

  const ariaLabel = `Select conversation scope, current: ${triggerLabel}`

  const emitScopeEvent = (target: string, action: 'shown' | 'click' | 'select', extra?: Record<string, unknown>) => {
    sendEvent('agent_sessions.task_view', {
      target,
      action,
      ...extra,
    })
  }

  const handleSelectPersonal = () => {
    if (selectedScope.kind === 'personal' && !retainedOwnerLogin) return
    emitScopeEvent('SCOPE_PICKER_ITEM_PERSONAL', 'select')
    onSelectScope({kind: 'personal'})
  }

  const handleSelectOrg = (org: AvailableOrganization) => {
    if (org.requiresSso) {
      emitScopeEvent('SCOPE_PICKER_ITEM_ORG', 'click', {requiresSso: true, orgLogin: org.login})
      // Persist the pending selection so the composer can re-apply it on the
      // SSO return. The caller consumes and clears this value on mount.
      setPendingSsoOrgLogin(org.login)
      navigateToSsoUrl(generateSSOUrl(org.login, location))
      return
    }
    if (selectedScope.kind === 'organization' && selectedScope.organization.id === org.id) return
    emitScopeEvent('SCOPE_PICKER_ITEM_ORG', 'select', {orgLogin: org.login})
    onSelectScope({kind: 'organization', organization: org})
  }

  return (
    <ActionMenu
      open={open}
      onOpenChange={next => {
        setOpen(next)
        if (next) emitScopeEvent('SCOPE_PICKER', 'shown')
      }}
    >
      <ActionMenu.Anchor>
        {showIconOnly ? (
          <IconButton
            type="button"
            icon={() => (
              <span className={styles.avatarLeadingVisual}>
                <ScopeAvatar
                  src={triggerAvatarSrc}
                  alt={triggerAvatarAlt}
                  kind={retainedOwnerLogin || selectedScope.kind === 'organization' ? 'organization' : 'user'}
                  size={16}
                />
              </span>
            )}
            aria-label={ariaLabel}
            variant="invisible"
            size={size}
            className={clsx(styles.selectorButton, triggerClassName)}
            disabled={disabled}
          />
        ) : (
          <Button
            type="button"
            leadingVisual={() => (
              <span className={styles.avatarLeadingVisual}>
                <ScopeAvatar
                  src={triggerAvatarSrc}
                  alt={triggerAvatarAlt}
                  kind={retainedOwnerLogin || selectedScope.kind === 'organization' ? 'organization' : 'user'}
                  size={16}
                />
              </span>
            )}
            trailingAction={TriangleDownIcon}
            aria-label={ariaLabel}
            variant="invisible"
            size={size}
            className={clsx(styles.selectorButton, triggerClassName)}
            disabled={disabled}
          >
            {triggerLabel}
          </Button>
        )}
      </ActionMenu.Anchor>
      <ActionMenu.Overlay width="medium" maxHeight="medium" overflow="auto">
        <ActionList selectionVariant="single">
          <ActionList.Item
            selected={selectedScope.kind === 'personal' && !retainedOwnerLogin}
            onSelect={handleSelectPersonal}
          >
            <ActionList.LeadingVisual>
              <ScopeAvatar src={currentUserAvatarUrl} alt={`${currentUserLogin} avatar`} kind="user" size={20} />
            </ActionList.LeadingVisual>
            {currentUserLogin}
          </ActionList.Item>
          {organizations.map(org => {
            const isSelected = selectedScope.kind === 'organization' && selectedScope.organization.id === org.id
            return (
              <ActionList.Item
                key={org.id}
                selected={org.requiresSso ? undefined : isSelected}
                role={org.requiresSso ? 'menuitem' : undefined}
                onSelect={() => handleSelectOrg(org)}
              >
                <ActionList.LeadingVisual>
                  <ScopeAvatar src={org.avatarUrl} alt={`${org.login} avatar`} kind="organization" size={20} />
                </ActionList.LeadingVisual>
                {org.login}
                {org.requiresSso && (
                  <ActionList.TrailingVisual>
                    <span className={styles.itemMeta}>
                      <LockIcon size={12} aria-label="SSO required" />
                    </span>
                  </ActionList.TrailingVisual>
                )}
              </ActionList.Item>
            )
          })}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

/**
 * The scope state the new-session composer hands its host.
 *
 * Declared structurally rather than imported so this module does not depend on
 * the composer package; it is assignable to the composer's `scopePicker` prop
 * either way.
 */
export interface ComposerScopePickerState {
  selectedScope: TaskComposerScope
  onSelectScope: (scope: TaskComposerScope) => void
  retainedOwnerLogin?: string
  organizations: AvailableOrganization[]
  currentUserLogin: string
  currentUserAvatarUrl?: string
  disabled: boolean
}

/**
 * The organization scope control every host of the new-session composer places.
 *
 * The composer decides *whether* a scope can be picked — it is offered for a
 * repoless Sandbox draft, Chat and task alike — while the host supplies the
 * control. The Agents page and the dashboard both render it from here so the two
 * surfaces cannot drift into offering different scope behavior for the same
 * draft.
 */
export function renderComposerScopePicker(state: ComposerScopePickerState): ReactNode {
  return (
    <ScopePicker
      selectedScope={state.selectedScope}
      onSelectScope={state.onSelectScope}
      retainedOwnerLogin={state.retainedOwnerLogin}
      organizations={state.organizations}
      currentUserLogin={state.currentUserLogin}
      currentUserAvatarUrl={state.currentUserAvatarUrl}
      forceIconOnly
      size="small"
      disabled={state.disabled}
    />
  )
}
